import { parseXmlNative } from '../../../utility/xmlHelpers';
import { getNodeValue, setNodeValue, xmlToString } from '../../../xmlUtility';
import {
    paramDsNameDest,
    paramDsNameSrc,
    paramList,
} from '../constants/parameterNames';
import { typeDataSource } from '../constants/parameterTypes';
import makeTaxonomyNamespaces from '../helpers/taxonomyNamespaces';

const parseXML = parseXmlNative;

const optionalParameters = [
    {
        name: paramList,
        description: 'Name of list to containing managed metadata columns',
    },
    {
        name: paramDsNameSrc,
        description: 'GetList data connection',
        type: typeDataSource,
    },
    {
        name: paramDsNameDest,
        description: 'GetTermSet data connection',
        type: typeDataSource,
    },
];
const requiredParameters = [];
const description =
    'Queries SharePoint to obtain a list of available TermSetsIds. Additional information on the metadata commands can be found on http://www.infopathdev.com/files/folders/qrules/entry76371.aspx.';

function GetTermSetIds(params) {
    const cf = params.commonFunction;
    const shp = params.shpAccess;
    const namespaces = makeTaxonomyNamespaces();

    const getTermsetsBasePath =
        '/dfs:myFields/dfs:queryFields/tns:GetTermSets/tns';
    const sspIdsXpath = getTermsetsBasePath + ':sharedServiceIds';
    const termSetIdsXpath = getTermsetsBasePath + ':termSetIds';
    const versionsXpath = getTermsetsBasePath + ':clientVersions';
    const timestampsXpath = getTermsetsBasePath + ':clientTimeStamps';
    const lcidXpath = getTermsetsBasePath + ':lcid';

    async function setQueryNodeValue(domDest, xPath, docElem) {
        let nodeItem = domDest.selectSingle(xPath, { namespaces });

        if (!nodeItem) {
            return;
        }

        await nodeItem.setValueAsync(xmlToString(docElem));
    }

    function createAndAppendChild(docElem, elemName, elmValue) {
        const elmChild = docElem.createElement(elemName);

        setNodeValue(elmChild, elmValue);

        docElem.documentElement.appendChild(elmChild);
    }

    async function setTermSetParamsAsync(domDest, infos) {
        let sbSIDDoc = parseXML('<sspIds/>');
        let sbTIDDoc = parseXML('<termSetIds/>');
        let sbTimeStampsDoc = parseXML('<dateTimes/>');
        let sbVersionsDoc = parseXML('<versions/>');

        for (const info of infos) {
            createAndAppendChild(sbSIDDoc, 'sspId', info.sspId);
            createAndAppendChild(sbTIDDoc, 'termSetId', info.termSetId);
            createAndAppendChild(
                sbTimeStampsDoc,
                'dateTime',
                '1900-01-01T00:00:00',
            );
            createAndAppendChild(sbVersionsDoc, 'version', '0');
        }

        let lcidNode = domDest.selectSingle(lcidXpath, { namespaces });

        if (lcidNode && !lcidNode.value()) {
            await lcidNode.setValueAsync('1033');
        }

        await setQueryNodeValue(domDest, sspIdsXpath, sbSIDDoc);
        await setQueryNodeValue(domDest, termSetIdsXpath, sbTIDDoc);
        await setQueryNodeValue(domDest, versionsXpath, sbVersionsDoc);
        await setQueryNodeValue(domDest, timestampsXpath, sbTimeStampsDoc);
    }

    function checkAndGetNodeValue(xpathEngine, field, xpathFilter, nodeName) {
        const path = xpathFilter + "'" + nodeName + "']";
        const node = xpathEngine
            .evaluateXPath(path, { context: field })
            .first();

        if (!node) {
            return null;
        }

        return getNodeValue(node);
    }

    function getTermSetInfoFromSchemaXml(xpathEngine, field, xpathFilter) {
        const termSetId = checkAndGetNodeValue(
            xpathEngine,
            field,
            xpathFilter,
            'TermSetId',
        );
        const anchorId = checkAndGetNodeValue(
            xpathEngine,
            field,
            xpathFilter,
            'AnchorId',
        );
        const sspId = checkAndGetNodeValue(
            xpathEngine,
            field,
            xpathFilter,
            'SspId',
        );

        return {
            anchorId: anchorId,
            sspId: sspId,
            termSetId: termSetId,
        };
    }

    const termInfoPresent = (infos, info) =>
        infos.some(
            (item) =>
                info.sspId === item.sspId &&
                info.termSetId === item.termSetId &&
                info.anchorId === item.anchorId,
        );

    function getTermsetInfos(fields) {
        const xpathFilter =
            'Field/Customization/ArrayOfProperty/Property/Value[../Name=';
        const xpathEngine = params.xpathEngine;
        let infos = [];

        for (const item of fields) {
            const field = parseXML(item.SchemaXml);
            const info = getTermSetInfoFromSchemaXml(
                xpathEngine,
                field,
                xpathFilter,
            );

            if (!termInfoPresent(infos, info)) {
                infos.push(info);
            }
        }

        return infos;
    }

    const determineSiteUrl = (dcList) => {
        const siteUrl = dcList.getUrl();
        const searchIndex = siteUrl.search(/_vti_bin\/lists.asmx/i);

        if (searchIndex) {
            return siteUrl.substr(0, searchIndex - 1);
        }

        return siteUrl;
    };

    async function getTermSetIdsAsync(dcList, domDest, list) {
        const siteUrl = determineSiteUrl(dcList);

        const result = await shp.getTaxonomyFieldsSchemaAsync(siteUrl, list);

        if (!result || !result.success || !result.data.length) {
            const errorMsg =
                result.error || 'Unable to parse schema for the list:' + list;

            throw new Error(errorMsg);
        }

        const infos = getTermsetInfos(result.data);

        await setTermSetParamsAsync(domDest, infos, namespaces);

        return {
            success: true,
        };
    }

    function executeAsync() {
        const list = cf.getParamValue(paramList, 'Shared Documents');
        const dsNameSrc = cf.getParamValue(paramDsNameSrc, 'GetList');
        const dsNameDest = cf.getParamValue(paramDsNameDest, 'GetTermSets');

        const dcList = cf.getDataConnection(dsNameSrc);

        const domDest = cf.getDataSource(dsNameDest);

        return getTermSetIdsAsync(dcList, domDest, list);
    }

    return {
        description,
        executeAsync,
        optionalParameters,
        requiredParameters,
    };
}

export default GetTermSetIds;
